import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getApplicationMessageApi,
  getBanksApi,
  submitRemarkApi,
  updateApplicationMessageApi,
  updateBankApi,
} from "../api/api";
import { useAuth } from "../context/AuthContext";

interface Bank {
  bank_id_pk: number;
  bank_name: string;
  bank_code?: string;
  branch_name?: string;
  ifsc_code?: string;
  bank_status?: number | string | boolean;
  created_by_login_id?: number;
  updated_by_login_id?: number;
}

// Normalizes bank_status to a strict 0 | 1 regardless of whether the API
// sends it as a number, a string ("0"/"1"), or a boolean.
const isBankOn = (bank: Bank) => Number(bank.bank_status) === 1;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState("");
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [updatingBankId, setUpdatingBankId] = useState<number | null>(null);
  // Per-bank error for the On/Off toggle, keyed by bank_id_pk
  const [bankErrors, setBankErrors] = useState<Record<number, string>>({});

  // Per-bank remark text, keyed by bank_id_pk
  const [remarks, setRemarks] = useState<Record<number, string>>({});
  // Per-bank submitting state, keyed by bank_id_pk
  const [submittingBankId, setSubmittingBankId] = useState<number | null>(null);
  // Per-bank success message, keyed by bank_id_pk
  const [successBankId, setSuccessBankId] = useState<number | null>(null);

  const [appMessageId, setAppMessageId] = useState<number | null>(null);
  const [appMessage, setAppMessage] = useState("");
  const [loadingAppMessage, setLoadingAppMessage] = useState(false);
  const [savingAppMessage, setSavingAppMessage] = useState(false);
  const [appMessageError, setAppMessageError] = useState("");
  const [appMessageSuccess, setAppMessageSuccess] = useState("");

  const isAdmin = (user?.role || "").toString().toLowerCase() === "admin";

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (!isAdmin) {
      navigate("/");
      return;
    }

    const loadBanks = async () => {
      try {
        setLoadingBanks(true);
        const response = await getBanksApi(user.token);
        const bankList = response?.data?.data || [];
        setBanks(Array.isArray(bankList) ? bankList : []);
      } catch (err: any) {
        console.error("Unable to load banks", err);
        setBanks([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    const loadApplicationMessage = async () => {
      try {
        setLoadingAppMessage(true);
        setAppMessageError("");
        const response = await getApplicationMessageApi(user.token);
        const data = response?.data || {};
        setAppMessageId(data?.id ?? null);
        setAppMessage(data?.message || "");
      } catch (err: any) {
        console.error("Unable to load application message", err);
        setAppMessageError(err?.message || "Unable to load application message");
      } finally {
        setLoadingAppMessage(false);
      }
    };

    loadBanks();
    loadApplicationMessage();
  }, [isAdmin, navigate, user]);

  const handleSaveApplicationMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.token) {
      setError("You are not logged in");
      return;
    }

    const trimmedMessage = appMessage.trim();
    if (!trimmedMessage) {
      setAppMessageError("Please enter application message");
      return;
    }

    try {
      setSavingAppMessage(true);
      setAppMessageError("");
      setAppMessageSuccess("");

      const body = await updateApplicationMessageApi(
        { message: trimmedMessage },
        user.token
      );

      if (!body?.success) {
        throw new Error(body?.message || "Failed to update application message");
      }

      const updated = body?.data || {};
      if (updated?.id !== undefined) {
        setAppMessageId(updated.id);
      }
      if (updated?.message !== undefined) {
        setAppMessage(updated.message || "");
      }
      setAppMessageSuccess("Application message saved");
      setTimeout(() => setAppMessageSuccess(""), 2500);
    } catch (err: any) {
      console.error("Unable to update application message", err);
      setAppMessageError(err?.message || "Unable to update application message");
    } finally {
      setSavingAppMessage(false);
    }
  };

  const setBankStatus = async (bank: Bank, desiredStatus: number) => {
    if (!user?.token) {
      setError("You are not logged in");
      return;
    }

    const currentStatus = isBankOn(bank) ? 1 : 0;
    if (currentStatus === desiredStatus) return;

    try {
      setUpdatingBankId(bank.bank_id_pk);
      setError("");
      setBankErrors((prev) => ({ ...prev, [bank.bank_id_pk]: "" }));

      const payload = {
        login_id: bank.created_by_login_id || 1,
        bank_name: bank.bank_name,
        bank_code: bank.bank_code || "",
        branch_name: bank.branch_name || "",
        ifsc_code: bank.ifsc_code || "",
        bank_status: desiredStatus === 1 ? 1 : 0,
        updated_by_login_id: bank.updated_by_login_id || bank.created_by_login_id || 1,
      };

      const res = await updateBankApi(bank.bank_id_pk, payload, user.token);

      if (res?.success === false) {
        throw new Error(res?.message || "Failed to update bank status");
      }

      setBanks((prevBanks) =>
        prevBanks.map((b) =>
          b.bank_id_pk === bank.bank_id_pk ? { ...b, bank_status: desiredStatus } : b
        )
      );
    } catch (err: any) {
      console.error(`Unable to update status for bank ${bank.bank_id_pk} (${bank.bank_name})`, err);
      setBankErrors((prev) => ({
        ...prev,
        [bank.bank_id_pk]: err.message || "Unable to update bank status",
      }));
    } finally {
      setUpdatingBankId(null);
    }
  };

  const handleRemarkChange = (bankId: number, value: string) => {
    setRemarks((prev) => ({ ...prev, [bankId]: value }));
  };

  const handleRemarkSubmit = async (e: React.FormEvent, bank: Bank) => {
    e.preventDefault();

    if (!user?.token) {
      setError("You are not logged in");
      return;
    }

    const remarkText = (remarks[bank.bank_id_pk] || "").trim();
    if (!remarkText) {
      setError("Please enter a remark");
      return;
    }

    try {
      setSubmittingBankId(bank.bank_id_pk);
      setSuccessBankId(null);
      setError("");

      const payload = {
        login_id: (user as any).id || bank.created_by_login_id || 1,
        bank_id: bank.bank_id_pk,
        bank_status: isBankOn(bank) ? 1 : 0,
        remark: remarkText,
      };

      const body = await submitRemarkApi(payload, user.token);

      // API response shape: { success: boolean, message: string, data: 200 }
      if (!body?.success) {
        throw new Error(body?.message || "Failed to submit remark");
      }

      setRemarks((prev) => ({ ...prev, [bank.bank_id_pk]: "" }));
      setSuccessBankId(bank.bank_id_pk);
      setTimeout(() => setSuccessBankId(null), 3000);
    } catch (err: any) {
      console.error(`Error submitting remark for bank ${bank.bank_id_pk} (${bank.bank_name})`, err);
      setError(err.message || "Unable to submit remark");
    } finally {
      setSubmittingBankId(null);
    }
  };

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div>
      <div className="card" style={{ marginTop: "24px" }}>
        <div className="settings-section">
          <div className="settings-section-title">App Settings</div>

          <form
            className="settings-row"
            onSubmit={handleSaveApplicationMessage}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "16px",
              display: "grid",
              gap: "12px",
            }}
          >
            <div className="settings-row-label">Application Message</div>
            {loadingAppMessage ? (
              <div className="settings-row-desc">Loading message...</div>
            ) : (
              <>
                {appMessageId !== null && (
                  <div className="settings-row-desc">Message ID: {appMessageId}</div>
                )}
                <textarea
                  value={appMessage}
                  onChange={(e) => setAppMessage(e.target.value)}
                  placeholder="Enter application message"
                  style={{
                    width: "100%",
                    minHeight: "76px",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
              </>
            )}

            {appMessageError && (
              <div style={{ color: "#d9534f", fontSize: 13 }}>{appMessageError}</div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loadingAppMessage || savingAppMessage}
                style={{ minWidth: "120px" }}
              >
                {savingAppMessage ? "Saving..." : "Save Message"}
              </button>
              {appMessageSuccess && (
                <span style={{ color: "#16a34a", fontSize: 13 }}>{appMessageSuccess}</span>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card" style={{ marginTop: "24px" }}>
        <div className="settings-section">
          <div className="settings-section-title">Bank Settings</div>

          {error && <div style={{ color: "#d9534f", marginBottom: 12, fontSize: 13 }}>{error}</div>}

          {loadingBanks ? (
            <div style={{ padding: "16px", textAlign: "center", color: "var(--text-muted)" }}>
              Loading banks...
            </div>
          ) : banks.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", color: "var(--text-muted)" }}>
              No banks available
            </div>
          ) : (
            <div style={{ display: "grid", gap: "16px" }}>
              {banks.map((bank) => {
                const on = isBankOn(bank);
                return (
                  <form
                    key={bank.bank_id_pk}
                    className="settings-row"
                    onSubmit={(e) => handleRemarkSubmit(e, bank)}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "16px",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: "16px",
                    }}
                  >
                    {/* Bank name + status */}
                    <div style={{ minWidth: "110px", flexShrink: 0 }}>
                      <div className="settings-row-label">{bank.bank_name}</div>
                      <div className="settings-row-desc" style={{ whiteSpace: "nowrap" }}>
                        {updatingBankId === bank.bank_id_pk
                          ? "Updating..."
                          : on
                          ? "Currently ON"
                          : "Currently OFF"}
                      </div>
                      {bankErrors[bank.bank_id_pk] && (
                        <div style={{ color: "#d9534f", fontSize: 12, marginTop: 4 }}>
                          {bankErrors[bank.bank_id_pk]}
                        </div>
                      )}
                    </div>

                    {/* On/Off buttons */}
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, marginTop: 2 }}>
                      <button
                        type="button"
                        className={`btn ${on ? "btn-primary" : ""}`}
                        onClick={() => setBankStatus(bank, 1)}
                        disabled={updatingBankId === bank.bank_id_pk}
                        aria-pressed={on}
                        title="Set On"
                      >
                        On
                      </button>
                      <button
                        type="button"
                        className={`btn ${!on ? "btn-danger" : ""}`}
                        onClick={() => setBankStatus(bank, 0)}
                        disabled={updatingBankId === bank.bank_id_pk}
                        aria-pressed={!on}
                        title="Set Off"
                      >
                        Off
                      </button>
                    </div>

                    {/* Remark textbox */}
                    <div style={{ flex: "1 1 220px", minWidth: "180px" }}>
                      <textarea
                        value={remarks[bank.bank_id_pk] || ""}
                        onChange={(e) => handleRemarkChange(bank.bank_id_pk, e.target.value)}
                        placeholder={`Enter remark for ${bank.bank_name}...`}
                        style={{
                          width: "100%",
                          minHeight: "40px",
                          maxHeight: "40px",
                          padding: "8px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "inherit",
                          resize: "vertical",
                        }}
                        required
                      />
                    </div>

                    {/* Submit button + success message */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginTop: 2 }}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submittingBankId === bank.bank_id_pk}
                        style={{ minWidth: "100px", whiteSpace: "nowrap" }}
                      >
                        {submittingBankId === bank.bank_id_pk ? "Submitting..." : "Submit"}
                      </button>
                      {successBankId === bank.bank_id_pk && (
                        <span style={{ color: "#16a34a", fontSize: 13, whiteSpace: "nowrap" }}>Saved</span>
                      )}
                    </div>
                  </form>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}